import Select, { SelectOption } from '@components/select';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Field } from 'components/Field';
import { FollowedLoadsPagination } from 'components/FollowedLoadsPagination';
import { useStore } from 'hooks/useStore';
import { AuctionIcon } from 'layout/Search/FollowedLoads/AuctionIcon';
import { PrettyLoad } from 'layout/Search/PrettyLoad';
import { TabsContent } from 'layout/TabsContent';

import css from './ContentWithAuctions.module.css';

const ContentWithAuctions = () => {
  const {
    app: { i18n },
    view,
    followedLoads: { favorites, loadsWithOffers, loadsWithAuctions },
    ui: { pagination, handleTabChange },
  } = useStore();

  const callbacks = useMemo(
    () => ({
      hideLoads: () => {},
      selectLoads: loadsWithAuctions.selectedLoads.toggleSelection,
      onRemoveLoadFromFavorites: favorites.updateLoads,
      onAddLoadToFavorites: favorites.updateLoads,
      onOfferChange: loadsWithOffers.updateLoads,
      onAuctionChange: loadsWithAuctions.updateLoads,
      onFilterTabOpen: handleTabChange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleItemsPerPageChange = (option: SelectOption) => {
    const newPage = Math.ceil(
      ((pagination.pages.onlyWithAuctions - 1) * pagination.loadsWithAuctionsPerPage.data + 1) / option.value,
    );
    pagination.setCurrentPage(newPage);
    pagination.loadsWithAuctionsPerPage.setOption(option);
    loadsWithAuctions.updateLoads();
  };

  if (loadsWithAuctions.count === 0) {
    return (
      <TabsContent.NoContent
        className={css.noContent}
        desc={''}
        icon={<AuctionIcon />}
        title={i18n.favorites.noContent.auctions.title}
      />
    );
  }

  return (
    <div>
      <div className={css.heading}>
        <h2 className={cn(css.totalItems, 'glz-h glz-is-size-2')}>
          {view.pluralizedCount(loadsWithAuctions.count, 'loads')}
        </h2>

        <FollowedLoadsPagination />
      </div>

      <div className={cn(css.actions, css.actions_top)}>
        <Field isRow labelClassName={css.sortLabel} labelText={i18n.app.showLines}>
          <Select
            className={css.sortSelect}
            dropdownWidth={{
              min: '100%',
              current: '100%',
            }}
            labelProperty="label"
            options={pagination.loadsWithAuctionsPerPage.options}
            usePortal={false}
            value={pagination.loadsWithAuctionsPerPage.option}
            onChange={handleItemsPerPageChange}
          />
        </Field>
      </div>

      <PrettyLoad
        callbacks={callbacks}
        currentPage={pagination.pages.onlyWithAuctions}
        descriptor={{ isFavorites: true }}
        loads={loadsWithAuctions.loads}
        updateLoad={loadsWithAuctions.updateLoad}
      />

      {pagination.totalLoadsWithAuctionsPages > 1 && (
        <div className={cn(css.actions, css.actions_bottom)}>
          <FollowedLoadsPagination />

          <Field containerClassName={css.sortField} labelClassName={css.sortLabel} labelText={i18n.app.showLines}>
            <Select
              className={css.sortSelect}
              dropdownWidth={{
                min: '100%',
                current: '100%',
              }}
              labelProperty="label"
              options={pagination.loadsWithAuctionsPerPage.options}
              usePortal={false}
              value={pagination.loadsWithAuctionsPerPage.option}
              onChange={handleItemsPerPageChange}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

const connected = observer(ContentWithAuctions);

export { connected as ContentWithAuctions };
